/**
 * Card 2 "Get Covered" send-off — after the match (pin at center, profile card
 * shown), the profile card fades, the pin lifts, then travels east drawing a
 * glowing cyan GPS route behind it ("your creator is en route") and exits the
 * right edge, then resets to center. Plays once after the match build-up (see
 * MatchAnimation). Transform/opacity only; honors prefers-reduced-motion.
 *
 * Percentages are of the send-off animation itself (own duration + delay).
 */
const TEAL = "#44C2E3";
const EXIT_X = "80cqw"; // east travel, card-relative (root sets container-type)
const WINDUP_X = "-6cqw"; // brief charge west before the launch
const EASE_ENERGETIC = "cubic-bezier(0.7, 0, 0.84, 0)";
const EASE_CALM = "cubic-bezier(0.65, 0, 0.35, 1)";
const EASE_WINDUP = "cubic-bezier(0.33, 0, 0.30, 1)";
const EASE_SOFT = "cubic-bezier(0.33, 0, 0.20, 1)";

// Send-off beats (% of the send animation).
const HANDOFF = 8; // send-off pin fades in as the match pin fades out
const LIFT = 18; // pin lifted
const WINDUP = 23; // brief charge west
const SEND_FADE = 46; // pin/glow stay visible until here
const TRAVEL_END = 62; // pin off the east edge, route fully drawn
const RECENTER = 74; // transport returns to center (invisible)
const ROUTE_FADE = 80;
const FACE_BACK = 88; // pin fades back in at center

/**
 * Keyframes + classes for the one-shot route-draw send-off and the hand-off
 * fades for the match pin and profile card. `sendMs`/`delayMs` place it after
 * the match build-up; `both` fill keeps the new elements hidden until then and
 * holds the resolved "pin at center" end state after.
 */
export const buildRouteCss = (sendMs: number, delayMs: number): string => `
@keyframes hiw-match-handoff { 0% { opacity: 1; } 100% { opacity: 0; } }

/* Pin shell carries the eastbound travel; it recenters while invisible. */
@keyframes hiw-match-send-pin {
  0%, ${LIFT}% { transform: translateX(0); animation-timing-function: ${EASE_WINDUP}; }
  ${WINDUP}% { transform: translateX(${WINDUP_X}); animation-timing-function: ${EASE_ENERGETIC}; }
  ${TRAVEL_END}% { transform: translateX(${EXIT_X}); }
  ${RECENTER}%, 100% { transform: translateX(0); }
}
/* Pin face: fade in (hand-off), lift, soft-fade off the edge, fade back center. */
@keyframes hiw-match-send-face {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(1); animation-timing-function: ${EASE_CALM}; }
  ${HANDOFF}% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  ${LIFT}% { opacity: 1; transform: translate(-50%, -50%) scale(1.16); animation-timing-function: ${EASE_ENERGETIC}; }
  ${SEND_FADE}% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
  ${TRAVEL_END}% { opacity: 0; transform: translate(-50%, -50%) scale(1.05); animation-timing-function: ${EASE_SOFT}; }
  ${FACE_BACK}% { opacity: 0; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}
/* Elevation glow — grows on the lift, travels with the pin. */
@keyframes hiw-match-send-glow {
  0%, ${HANDOFF}% { opacity: 0; transform: translate(-50%, -50%) scale(1); animation-timing-function: ${EASE_ENERGETIC}; }
  ${LIFT}% { opacity: 0.34; transform: translate(-50%, -50%) scale(1.55); }
  ${SEND_FADE}% { opacity: 0.3; transform: translate(-50%, -50%) scale(1.5); }
  ${TRAVEL_END}% { opacity: 0; transform: translate(-50%, -50%) scale(1.4); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1); }
}
/* Glowing GPS route, drawn from center eastward as the pin travels. */
@keyframes hiw-match-route {
  0%, ${WINDUP}% { opacity: 0; transform: translateY(-50%) scaleX(0); animation-timing-function: ${EASE_ENERGETIC}; }
  ${WINDUP + 1}% { opacity: 0.85; transform: translateY(-50%) scaleX(0.02); }
  ${TRAVEL_END}% { opacity: 0.85; transform: translateY(-50%) scaleX(1); animation-timing-function: ${EASE_CALM}; }
  ${ROUTE_FADE}% { opacity: 0; transform: translateY(-50%) scaleX(1); }
  100% { opacity: 0; transform: translateY(-50%) scaleX(0); }
}

.hiw-match-send-el { will-change: transform, opacity; }
.hiw-match-send-pin { animation: hiw-match-send-pin ${sendMs}ms linear ${delayMs}ms both; }
.hiw-match-send-face { animation: hiw-match-send-face ${sendMs}ms linear ${delayMs}ms both; }
.hiw-match-send-glow { animation: hiw-match-send-glow ${sendMs}ms linear ${delayMs}ms both; }
.hiw-match-route { animation: hiw-match-route ${sendMs}ms linear ${delayMs}ms both; }
/* Match pin keeps pulsing, then fades out as the send-off pin takes over. */
.hiw-match-pin-send { animation: hiw-match-pin-active 1.6s ease-in-out infinite, hiw-match-handoff 700ms linear ${delayMs}ms forwards; }
/* Profile card slides in (match), then fades out for the send-off. */
.hiw-match-card-send { animation: hiw-match-card 7s ease-out forwards, hiw-match-handoff 700ms linear ${delayMs}ms forwards; }

@media (prefers-reduced-motion: reduce) {
  /* Static matched state: pin + card shown; nothing sends. */
  .hiw-match-send-el { animation: none !important; opacity: 0 !important; transform: translate(-50%, -50%) !important; }
  .hiw-match-route { animation: none !important; opacity: 0 !important; }
  .hiw-match-pin-send, .hiw-match-card-send { animation: none !important; }
}
`;

export const ROUTE_TEAL = TEAL;
